if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.items = Number(this.dataset.items);
        this.showDotsInitially = this.classList.contains('slideshow-controls-visible--yes');
        this.expirationTimeout = null;
        this.onVisibilityChange = () => {
          if (!document.hidden) this.refreshScheduledSlides();
        };
        this.onPageShow = () => this.refreshScheduledSlides();
      }

      connectedCallback() {
        if (this.sliderInstance) return;

        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('pageshow', this.onPageShow);
        this.refreshScheduledSlides();
      }

      disconnectedCallback() {
        clearTimeout(this.expirationTimeout);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('pageshow', this.onPageShow);

        if (this.sliderInstance?.slider && !this.sliderInstance.slider.destroyed) {
          this.sliderInstance.slider.destroy(true, true);
        }
        this.sliderInstance = false;
      }

      // Interpret the date and time entered in Customize in Europe/Bucharest,
      // independently of the visitor's browser time zone. Romania uses UTC+2
      // in winter and UTC+3 in summer; Intl checks the applicable DST offset.
      parseBucharestExpiration(dateString, timeString) {
        const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
        const timeMatch = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeString);
        if (!dateMatch || !timeMatch) return null;

        const [, yearText, monthText, dayText] = dateMatch;
        const [, hourText, minuteText] = timeMatch;
        const year = Number(yearText);
        const month = Number(monthText);
        const day = Number(dayText);
        const hour = Number(hourText);
        const minute = Number(minuteText);
        const nominalUtc = Date.UTC(year, month - 1, day, hour, minute);
        const parsedDate = new Date(nominalUtc);
        if (
          parsedDate.getUTCFullYear() !== year ||
          parsedDate.getUTCMonth() !== month - 1 ||
          parsedDate.getUTCDate() !== day
        ) return null;

        const formatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/Bucharest',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        });
        const expected = `${dateString} ${timeString}`;
        const matches = [2, 3]
          .map((hoursOffset) => nominalUtc - hoursOffset * 60 * 60 * 1000)
          .filter((timestamp) => {
            const parts = Object.fromEntries(
              formatter.formatToParts(new Date(timestamp))
                .filter((part) => part.type !== 'literal')
                .map((part) => [part.type, part.value])
            );
            return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}` === expected;
          });

        // When the clock moves back, an hour occurs twice: use its later occurrence.
        return matches.length ? Math.max(...matches) : null;
      }

      getSlideExpiration(slide) {
        if (!slide.dataset.expireDate) return null;
        if (slide.dataset.expirationTimestamp) {
          return Number(slide.dataset.expirationTimestamp);
        }

        const timestamp = this.parseBucharestExpiration(
          slide.dataset.expireDate,
          slide.dataset.expireTime || '23:59'
        );
        if (timestamp !== null) slide.dataset.expirationTimestamp = String(timestamp);
        return timestamp;
      }

      refreshScheduledSlides() {
        clearTimeout(this.expirationTimeout);
        this.expirationTimeout = null;

        const wrapper = this.querySelector('.swiper-wrapper');
        if (!wrapper) return;

        const getSlides = () => Array.from(wrapper.children).filter((item) =>
          item.classList.contains('swiper-slide') &&
          !item.classList.contains('swiper-slide-duplicate') &&
          !item.classList.contains('swiper-slide-blank')
        );
        const now = Date.now();
        const expiredSlides = getSlides().filter((slide) => {
          const expiration = this.getSlideExpiration(slide);
          return expiration !== null && now >= expiration;
        });

        let previouslySelectedBlock = null;
        if (expiredSlides.length && this.sliderInstance?.slider) {
          const swiper = this.sliderInstance.slider;
          previouslySelectedBlock = swiper.slides[swiper.activeIndex]?.dataset.index ?? null;
          if (!swiper.destroyed) swiper.destroy(true, true);
          this.sliderInstance = false;
        }

        let nextExpiration = Infinity;
        getSlides().forEach((slide) => {
          const expiration = this.getSlideExpiration(slide);
          if (expiration !== null && Date.now() >= expiration) {
            slide.remove();
          } else {
            // Hide scheduled slides until their validity was checked to avoid flashing expired banners.
            slide.classList.remove('slideshow-slide--scheduled');
            if (expiration !== null) nextExpiration = Math.min(nextExpiration, expiration);
          }
        });

        const remainingSlides = getSlides();
        this.items = remainingSlides.length;
        this.dataset.items = String(this.items);
        remainingSlides.forEach((slide, index) => {
          slide.setAttribute('aria-label', `${index + 1} ${this.dataset.slideOf || 'of'} ${this.items}`);
        });
        this.closest('.section--slideshow')?.classList.toggle('slideshow-section--empty', this.items === 0);
        this.classList.toggle('slideshow-controls-visible--yes', this.showDotsInitially && this.items > 1);

        if (this.items === 0) {
          this.removeAttribute('data-media-loading');
        } else if (!this.sliderInstance) {
          this.initSlider();
          if (previouslySelectedBlock !== null) {
            const slideIndex = remainingSlides.findIndex((slide) => slide.dataset.index === previouslySelectedBlock);
            const swiper = this.sliderInstance?.slider;
            if (slideIndex > 0 && swiper) {
              if (swiper.params.loop) swiper.slideToLoop(slideIndex, 0, false);
              else swiper.slideTo(slideIndex, 0, false);
            }
          }
          if (this.classList.contains('slideshow-height--adapt')) this.calcSlideHeight();
        }

        if (Number.isFinite(nextExpiration)) {
          // Browsers cap setTimeout at about 24.8 days; recheck longer schedules in chunks.
          const delay = Math.max(50, Math.min(nextExpiration - Date.now() + 25, 2147483647));
          this.expirationTimeout = setTimeout(() => this.refreshScheduledSlides(), delay);
        }
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        try {
          const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

          this.sliderOptions = {
            slidesPerView: 1,
            spaceBetween: 10,
            loop: true,
            grabCursor: true,
            allowTouchMove: true,
            threshold: 2,
            effect: 'fade',
            on: {
              init: this.handleAfterInit.bind(this),
            },
            fadeEffect: {
              crossFade: true,
            },
          };

          if (this.sliderControls) {
            this.sliderOptions.pagination = {
              el: this.sliderControls.querySelector('.swiper-pagination'),
              clickable: true,
            };
          }

          switch (this.layout) {
            case 'centered':
              this.sliderOptions = {
                ...this.sliderOptions,
                centeredSlides: true,
                slidesPerView: 'auto',
                effect: 'slide',
              };
              break;
          }

          if (this.items === 1) {
            this.sliderOptions.loop = false;
            this.classList.remove('slideshow-controls-visible--yes');
          }

          const autoplayDelay = parseInt(this.dataset.autoplay);
          if (autoplayDelay > 0) {
            this.sliderOptions = {
              ...this.sliderOptions,
              autoplay: {
                delay: autoplayDelay,
                disableOnInteraction: false,
              },
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
          this.sliderInstance.init();

          this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));
          this.selectedIndex = this.sliderInstance.slider.realIndex;

          if (this.sliderInstance) {
            if (this.layout === 'centered' && !FoxTheme.config.mqlMobile) {
              const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
              motionTextElements &&
                motionTextElements.forEach((motionTextElement) => {
                  if (
                    typeof motionTextElement.resetAnimation === 'function' &&
                    !motionTextElement.closest('.swiper-slide-active')
                  ) {
                    motionTextElement.resetAnimation();
                  }
                });
            }
            this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
            if (this.selectedElement) this.updateControlsScheme(this.selectedElement);
            this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
            // Fix accessibility
            const focusableElements = FoxTheme.a11y.getFocusableElements(this);
            if (this.layout === 'centered') {
              focusableElements.forEach((element) => {
                element.addEventListener('focusin', () => {
                  const slide = element.closest('.swiper-slide');
                  const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
                  this.sliderInstance.slider.slideToLoop(slideToIndex);
                });
              });
            } else {
              focusableElements.forEach((element) => {
                element.addEventListener('focusin', () => {
                  const slide = element.closest('.swiper-slide');
                  this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
                });
              });
            }
          }
        } finally {
          this.removeAttribute('data-media-loading');
        }
      }

      onReady(selectedElement) {
        if (selectedElement?.dataset?.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement?.querySelectorAll('motion-element');
          motionEls?.forEach((motionEl) => {
            if (!motionEl || motionEl.classList.contains('is-animated')) return;

            motionEl && motionEl?.removeAttribute('hold');
            motionEl && motionEl?.preInitialize();

            setTimeout(() => {
              motionEl?.initialize();
            });
          });
        }
      }

      handleAfterInit() {
        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls.forEach((motionEl) => {
              if (motionEl.hasAttribute('data-text')) {
                motionEl.resetAnimation();
              }
            });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  motionEl && motionEl.refreshAnimation();
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) {
                maxHeight = slideTextHeight;
              }
            }
          });

        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100; // Avoid overlap with dots.
        }

        this.style.setProperty('--slide-height', maxHeight + 'px');
      }
    }
  );
}
